import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import {
    ArrowDown,
    ArrowUp,
    Camera,
    DotsSixVertical,
    Eye,
    EyeSlash,
    X,
} from 'phosphor-react'
import { Checkbox, Form } from 'semantic-ui-react'
import {
    ConsolidatedReportScheduleService,
    ControlType,
    TemplateService,
} from 'utility'
import { AppButton } from 'elements'
import {
    BUILTIN_EMAIL_COLUMNS,
    MAX_TEMPLATE_COLUMNS,
    columnHeading,
} from './consolidated-report-email-utils'
import {
    appendFields,
    copyOrderToOthers,
    fieldsOf,
    groupKeyOf,
    isShownInBlock,
    moveInTemplate,
    normalizeText,
    removeColumnByKey,
    removeTemplateFields,
    sharedQuestions,
    standardOrderChanged,
    stepInTemplate,
    templateSequence,
} from './email-content-utils'
import { NewBadge } from './consolidated-report-email-styles'

const builtinMeta = Object.fromEntries(
    BUILTIN_EMAIL_COLUMNS.map(column => [column.key, column]),
)

// Template details are fetched for every chosen template (fields + "same field" box); cap it.
const MAX_LOADED_TEMPLATES = 12

const stripHtml = value =>
    String(value || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/\s+/g, ' ')
        .trim()

const controlTypeLabel = question => {
    const raw = question?.ControlType ?? question?.QuestionControlType
    const match = Object.entries(ControlType).find(
        ([, id]) => Number(id) === Number(raw),
    )
    if (!match) return ''
    if (match[0] === 'TextAnswer' || match[0] === 'TextArea') return 'Text'
    if (match[0] === 'MulltiResponse') return 'Choice'
    if (match[0] === 'DateAndTime') return 'Date'
    return match[0]
}

const parseQuestions = detail => {
    const list = []
    ;(detail?.Sections || []).forEach(section => {
        const sectionName = section.SectionName || section.Name || ''
        ;(section.Questions || []).forEach(question => {
            const control = Number(question.ControlType ?? question.QuestionControlType)
            if (control === ControlType.Header || control === ControlType.Label) return
            if (Number(question.ChangeAction) === 3) return
            const id = question.Id ?? question.QuestionId
            const text = stripHtml(question.Question || question.QuestionText)
            if (!id || !text) return
            list.push({
                id,
                text,
                section: sectionName,
                responseType: controlTypeLabel(question),
            })
        })
    })
    return list
}

const highlight = (text, query) => {
    const q = String(query || '').trim()
    if (!q) return text
    const at = text.toLowerCase().indexOf(q.toLowerCase())
    if (at < 0) return text
    return (
        <>
            {text.slice(0, at)}
            <mark>{text.slice(at, at + q.length)}</mark>
            {text.slice(at + q.length)}
        </>
    )
}

const Wrap = styled.div`
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    padding: 16px;
    background: #fff;
    margin: 6px 0 18px;

    mark {
        background: #fde68a;
        border-radius: 3px;
        padding: 0 1px;
    }
`

const StepHead = styled.div`
    display: flex;
    gap: 10px;
    align-items: flex-start;
    margin: 4px 0 10px;

    .num {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background: ${({ theme }) => theme.colors?.primary || '#263BA6'};
        color: #fff;
        font-weight: 700;
        font-size: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 auto;
    }
    h4 {
        margin: 0;
        font-size: 15px;
    }
    small {
        display: block;
        color: #6b7280;
        font-size: 12px;
        margin-top: 2px;
    }
`

const Two = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 14px;
    margin-bottom: 18px;

    @media (max-width: 1100px) {
        grid-template-columns: minmax(0, 1fr);
    }
`

const Box = styled.div`
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    background: #fff;
    display: flex;
    flex-direction: column;
    min-width: 0;

    .box-h {
        padding: 10px 12px;
        border-bottom: 1px solid #e5e7eb;
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
        font-weight: 600;
        font-size: 13px;
    }
    .box-b {
        padding: 6px;
        max-height: 420px;
        overflow: auto;
        flex: 1;
    }
    .box-f {
        padding: 8px 12px;
        border-top: 1px solid #e5e7eb;
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
        font-size: 12px;
        color: #6b7280;
    }
`

const SearchInput = styled.input`
    width: 100%;
    border: 1px solid #e5e7eb;
    border-radius: 9px;
    padding: 7px 10px;
    font-size: 14px;
    outline: none;

    &:focus {
        border-color: ${({ theme }) => theme.colors?.primary || '#263BA6'};
    }
`

const Avail = styled.label`
    display: flex !important;
    align-items: center;
    gap: 9px;
    padding: 7px 8px;
    border-radius: 8px;
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
    opacity: ${({ $dim }) => ($dim ? 0.55 : 1)};
    font-weight: 400 !important;
    margin: 0 !important;

    &:hover {
        background: #f1f5ff;
    }
    input {
        width: 16px;
        height: 16px;
        margin: 0;
        flex: 0 0 auto;
    }
    .t {
        flex: 1;
        min-width: 0;
        overflow-wrap: anywhere;
        font-size: 13px;
        color: #111827;
    }
    .cnt {
        color: #6b7280;
        font-size: 12px;
        white-space: nowrap;
    }
    .also {
        display: block;
        font-size: 11px;
        color: #047857;
        font-weight: 600;
    }
`

const SectionLabel = styled.div`
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.05em;
    color: #9ca3af;
    margin: 10px 8px 4px;
    text-transform: uppercase;
`

const TypePill = styled.span`
    font-size: 11px;
    font-weight: 600;
    border-radius: 999px;
    padding: 1px 8px;
    background: #eef2ff;
    color: #3730a3;
    white-space: nowrap;
`

const Item = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 8px;
    border: 1px solid ${({ $field }) => ($field ? '#c7d0f5' : '#e5e7eb')};
    border-radius: 9px;
    margin: 5px 2px;
    background: ${({ $field }) => ($field ? '#eef1fd' : '#f8fafc')};

    .grip {
        color: #94a3b8;
        display: inline-flex;
        cursor: grab;
    }
    .pos {
        width: 22px;
        height: 22px;
        border-radius: 6px;
        background: ${({ $field, theme }) =>
            $field ? theme.colors?.primary || '#263BA6' : '#94a3b8'};
        color: #fff;
        font-size: 11px;
        font-weight: 700;
        display: flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 auto;
    }
    .t {
        flex: 1;
        min-width: 0;
        overflow-wrap: anywhere;
        font-size: 13px;
    }
    .t small {
        display: block;
        color: #6b7280;
        font-size: 11px;
    }
    .name {
        cursor: text;
        font-weight: ${({ $field }) => ($field ? 600 : 500)};
    }
    input.ren {
        border: 1px solid #263ba6;
        border-radius: 6px;
        padding: 3px 6px;
        width: 100%;
        font-size: 13px;
    }
`

const IconBtn = styled.button`
    border: 1px solid transparent;
    background: none;
    padding: 3px 5px;
    border-radius: 6px;
    color: ${({ $on }) => ($on ? '#263BA6' : '#64748b')};
    line-height: 1;
    display: inline-flex;
    cursor: pointer;

    &:hover:not(:disabled) {
        border-color: #e5e7eb;
        background: #fff;
    }
    &:disabled {
        opacity: 0.3;
        cursor: default;
    }
`

const Tag = styled.span`
    font-size: 10px;
    font-weight: 700;
    border-radius: 999px;
    padding: 1px 6px;
    background: #e2e8f0;
    color: #475569;
    white-space: nowrap;
`

const Pill = styled.span`
    font-size: 11px;
    font-weight: 700;
    border-radius: 999px;
    padding: 2px 8px;
    white-space: nowrap;
    background: ${({ $tone }) =>
        ({ warn: '#fef3c7', brand: '#dbeafe' }[$tone] || '#f3f4f6')};
    color: ${({ $tone }) =>
        ({ warn: '#b45309', brand: '#1d4ed8' }[$tone] || '#6b7280')};
`

const Tabs = styled.div`
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    margin-bottom: 10px;
`

const Tab = styled.button`
    border: 1px solid ${({ $on }) => ($on ? '#263BA6' : '#e5e7eb')};
    background: ${({ $on }) => ($on ? '#eef1fd' : '#fff')};
    color: ${({ $on }) => ($on ? '#263BA6' : '#374151')};
    border-radius: 10px;
    padding: 7px 12px;
    font-weight: 600;
    display: inline-flex;
    gap: 8px;
    align-items: center;
    cursor: pointer;
    text-align: left;
`

const Common = styled.div`
    border: 1px solid #a7f3d0;
    background: #ecfdf5;
    border-radius: 12px;
    padding: 10px 12px;
    margin-bottom: 12px;

    .crow {
        display: flex;
        align-items: center;
        gap: 6px;
        flex-wrap: wrap;
        padding: 5px 0;
        border-top: 1px dashed #a7f3d0;
    }
    .crow:first-of-type {
        border-top: none;
    }
    .cn {
        font-weight: 650;
        min-width: 110px;
    }
`

const Chip = styled.button`
    font-size: 12px;
    border-radius: 999px;
    padding: 2px 9px;
    border: 1px solid
        ${({ $state }) =>
            ({ ok: '#6ee7b7', add: '#c7d0f5' }[$state] || '#d1d5db')};
    background: ${({ $state }) => ($state === 'ok' ? '#d1fae5' : '#fff')};
    color: ${({ $state }) =>
        ({ ok: '#047857', add: '#263BA6' }[$state] || '#6b7280')};
    cursor: ${({ $state }) => ($state === 'add' ? 'pointer' : 'default')};
`

const Notice = styled.div`
    font-size: 12px;
    border-radius: 9px;
    padding: 8px 10px;
    margin: 8px 0;
    display: flex;
    gap: 12px;
    align-items: center;
    justify-content: space-between;
    background: ${({ $tone }) =>
        ({ warn: '#fffbeb', info: '#f8fafc', dark: '#111827' }[$tone] ||
        '#f8fafc')};
    color: ${({ $tone }) =>
        ({ warn: '#92400e', info: '#475569', dark: '#fff' }[$tone] ||
        '#475569')};
    border: 1px solid
        ${({ $tone }) =>
            ({ warn: '#fde68a', info: '#e5e7eb', dark: '#111827' }[$tone] ||
            '#e5e7eb')};

    button {
        border: none;
        background: none;
        font-weight: 700;
        color: ${({ $tone }) => ($tone === 'dark' ? '#a5b4fc' : '#263BA6')};
        cursor: pointer;
        white-space: nowrap;
    }
`

export const EmailContentSection = ({
    includeAllTemplates,
    templateIds = [],
    templateOrderSaved = false,
    columns = [],
    onChange,
    templateOptions = [],
    templateRecords = [],
    templateError,
}) => {
    const [usage, setUsage] = useState(null)
    const [templateQuery, setTemplateQuery] = useState('')
    const [fieldQuery, setFieldQuery] = useState('')
    const [activeKey, setActiveKey] = useState(null)
    const [picked, setPicked] = useState({})
    const [questionsByTemplate, setQuestionsByTemplate] = useState({})
    const [loadState, setLoadState] = useState({})
    const [editing, setEditing] = useState(null)
    const [editText, setEditText] = useState('')
    const [editStart, setEditStart] = useState('')
    const renameCancelled = useRef(false)
    const [extraTabs, setExtraTabs] = useState([])
    const [undo, setUndo] = useState(null)
    const [info, setInfo] = useState('')
    const [showAllShared, setShowAllShared] = useState(false)
    const loading = useRef(new Set())
    const undoTimer = useRef(null)

    // Template copies often share a name; add the template number when it repeats.
    const nameOf = useMemo(() => {
        const map = new Map()
        const seen = new Map()
        ;(templateOptions || []).forEach(option => {
            const text = option.text || ''
            seen.set(text, (seen.get(text) || 0) + 1)
        })
        ;(templateOptions || []).forEach(option => {
            const text = option.text || ''
            map.set(String(option.value), seen.get(text) > 1 ? `${text} (#${option.value})` : text)
        })
        return key => map.get(String(key)) || `Template ${key}`
    }, [templateOptions])

    const recordOf = key =>
        (templateRecords || []).find(record => String(record.Id) === String(key))

    // Templates that have fields saved (key = numeric template id as a string).
    const fieldTemplateKeys = useMemo(() => {
        const keys = []
        ;(columns || []).forEach(column => {
            const key = groupKeyOf(column, templateRecords)
            if (key && !keys.includes(key)) keys.push(key)
        })
        return keys
    }, [columns, templateRecords])

    // Chosen templates: the schedule's list (in block order), or with All templates the ones
    // that have fields plus the ones ticked to pick fields for.
    const chosen = useMemo(() => {
        if (!includeAllTemplates) return (templateIds || []).map(String)
        const keys = [...fieldTemplateKeys]
        extraTabs.forEach(key => {
            if (!keys.includes(key)) keys.push(key)
        })
        return keys
    }, [includeAllTemplates, templateIds, fieldTemplateKeys, extraTabs])

    useEffect(() => {
        let alive = true
        ConsolidatedReportScheduleService.templateUsage(30)
            .then(result => {
                if (!alive) return
                const map = {}
                ;(result?.Items || []).forEach(row => {
                    map[String(row.TemplateId)] = Number(row.Audits) || 0
                })
                setUsage(map)
            })
            .catch(() => {
                if (alive) setUsage(null)
            })
        return () => {
            alive = false
        }
    }, [])

    useEffect(() => {
        if (!chosen.length) {
            setActiveKey(null)
            return
        }
        if (!chosen.includes(String(activeKey))) setActiveKey(chosen[0])
    }, [chosen, activeKey])

    const loadQuestions = async key => {
        if (loading.current.has(key)) return
        loading.current.add(key)
        setLoadState(prev => ({ ...prev, [key]: 'loading' }))
        try {
            const record = recordOf(key)
            const detail = await TemplateService.getTemplateDetails(
                record?.TemplateId || key,
            )
            setQuestionsByTemplate(prev => ({ ...prev, [key]: parseQuestions(detail) }))
            setLoadState(prev => ({ ...prev, [key]: 'ok' }))
        } catch {
            setLoadState(prev => ({ ...prev, [key]: 'error' }))
        } finally {
            loading.current.delete(key)
        }
    }

    useEffect(() => {
        if (!(templateRecords || []).length) return
        const wanted = chosen.slice(0, MAX_LOADED_TEMPLATES)
        if (activeKey && !wanted.includes(String(activeKey))) wanted.push(String(activeKey))
        wanted.forEach(key => {
            if (!loadState[key]) loadQuestions(key)
        })
    }, [chosen, activeKey, templateRecords, loadState])

    useEffect(() => () => clearTimeout(undoTimer.current), [])

    const showUndo = (message, restore) => {
        clearTimeout(undoTimer.current)
        setUndo({ message, restore })
        undoTimer.current = setTimeout(() => setUndo(null), 10000)
    }

    // Every edit goes through change(): a pending Undo is dropped, because its snapshot is older
    // than this edit and restoring it would throw the edit away.
    const change = patch => {
        clearTimeout(undoTimer.current)
        setUndo(null)
        onChange(patch)
    }

    const apply = (nextColumns, patch = {}) => {
        if (standardOrderChanged(columns, nextColumns) && chosen.length > 1) {
            setInfo(
                'Standard columns keep one order in every block, so the other templates were updated too.',
            )
        }
        change({ columns: nextColumns, ...patch })
    }

    const groupOrder = chosen

    // ---------- templates ----------
    const sortedTemplates = useMemo(() => {
        const list = [...(templateOptions || [])]
        list.sort((a, b) => {
            const ua = usage ? usage[String(a.value)] || 0 : 0
            const ub = usage ? usage[String(b.value)] || 0 : 0
            if (ub !== ua) return ub - ua
            return String(a.text || '').localeCompare(String(b.text || ''))
        })
        return list
    }, [templateOptions, usage])

    const filteredTemplates = useMemo(() => {
        const q = templateQuery.trim().toLowerCase()
        if (!q) return sortedTemplates
        return sortedTemplates.filter(option =>
            String(option.text || '').toLowerCase().includes(q),
        )
    }, [sortedTemplates, templateQuery])

    const addTemplate = key => {
        if (includeAllTemplates) {
            setExtraTabs(prev => (prev.includes(key) ? prev : [...prev, key]))
        } else {
            change({ templateIds: [...(templateIds || []), Number(key)] })
        }
        setActiveKey(key)
        setPicked({})
        setFieldQuery('')
    }

    const removeTemplate = key => {
        const fieldCount = fieldsOf(columns, key, templateRecords).length
        const before = {
            templateIds: [...(templateIds || [])],
            columns,
            extraTabs: [...extraTabs],
        }
        const nextColumns = removeTemplateFields(columns, key, templateRecords)
        if (includeAllTemplates) {
            setExtraTabs(prev => prev.filter(item => item !== key))
            change({ columns: nextColumns })
        } else {
            change({
                templateIds: (templateIds || []).filter(id => String(id) !== key),
                columns: nextColumns,
            })
        }
        showUndo(
            `Removed ${nameOf(key)}${
                fieldCount
                    ? ` and its ${fieldCount} field${fieldCount > 1 ? 's' : ''}`
                    : ''
            }.`,
            () => {
                setExtraTabs(before.extraTabs)
                onChange({ templateIds: before.templateIds, columns: before.columns })
            },
        )
    }

    const toggleTemplate = (key, checked) => {
        if (checked) addTemplate(key)
        else removeTemplate(key)
    }

    const setAllTemplates = checked => {
        if (checked) {
            setExtraTabs((templateIds || []).map(String))
            change({ includeAllTemplates: true, templateIds: [], templateOrderSaved: false })
        } else {
            // Keep every template that has fields (or a tab) so nothing is lost.
            change({
                includeAllTemplates: false,
                templateIds: chosen.map(Number).filter(Number.isFinite),
            })
            setExtraTabs([])
        }
    }

    const moveTemplate = (from, to) => {
        const list = [...(templateIds || [])]
        if (to < 0 || to >= list.length) return
        const [moved] = list.splice(from, 1)
        list.splice(to, 0, moved)
        change({ templateIds: list, templateOrderSaved: true })
    }

    // ---------- fields ----------
    const activeQuestions = activeKey ? questionsByTemplate[activeKey] || [] : []
    const activeFields = activeKey ? fieldsOf(columns, activeKey, templateRecords) : []
    const activeQuestionIds = new Set(activeFields.map(column => String(column.questionId)))
    const pickedCount = Object.keys(picked).length
    const left = MAX_TEMPLATE_COLUMNS - activeFields.length - pickedCount

    const otherTemplatesWith = text => {
        const target = normalizeText(text)
        return chosen
            .filter(key => key !== activeKey)
            .filter(key =>
                (questionsByTemplate[key] || []).some(
                    question => normalizeText(question.text) === target,
                ),
            )
            .map(nameOf)
    }

    const newColumn = (key, question) => ({
        key: `question:${question.id}`,
        builtin: false,
        locked: false,
        visible: true,
        label: '',
        questionId: question.id,
        templateId: Number(key),
        questionText: question.text,
        templateName: nameOf(key),
        responseType: question.responseType || '',
        showPhotos: false,
    })

    const addPicked = () => {
        const toAdd = activeQuestions
            .filter(question => picked[question.id])
            .slice(0, Math.max(0, MAX_TEMPLATE_COLUMNS - activeFields.length))
            .map(question => newColumn(activeKey, question))
        apply(appendFields(columns, groupOrder, templateRecords, activeKey, toAdd))
        setPicked({})
        setInfo(
            `Added ${toAdd.length} field${toAdd.length === 1 ? '' : 's'} at the end. Drag them where you want them.`,
        )
    }

    const addToTemplate = (key, question) => {
        if (fieldsOf(columns, key, templateRecords).length >= MAX_TEMPLATE_COLUMNS) return columns
        return appendFields(columns, groupOrder, templateRecords, key, [
            newColumn(key, question),
        ])
    }

    const removeField = column => {
        const before = columns
        apply(removeColumnByKey(columns, column.key))
        showUndo(`Removed "${columnHeading(column) || column.questionText}".`, () =>
            onChange({ columns: before }),
        )
    }

    const updateColumn = (key, patch) =>
        change({
            columns: columns.map(column =>
                column.key === key ? { ...column, ...patch } : column,
            ),
        })

    // The box starts with the header as it is today (a blank "Report link" header stays blank).
    // Nothing is saved unless the text was actually changed.
    const startRename = column => {
        const current = columnHeading(column) || ''
        renameCancelled.current = false
        setEditing(column.key)
        setEditText(current)
        setEditStart(current)
    }

    const commitRename = column => {
        setEditing(null)
        if (renameCancelled.current) return
        const typed = editText.trim()
        if (typed === editStart.trim()) return
        let label = typed
        if (column.builtin) {
            if (typed === (builtinMeta[column.key]?.defaultLabel || '')) label = ''
        } else if (!typed || typed === column.questionText) {
            label = ''
        } else {
            label = typed.slice(0, 100)
        }
        updateColumn(column.key, { label })
    }

    const activeSequence = activeKey
        ? templateSequence(columns, activeKey, templateRecords)
        : []
    const shownSequence = activeSequence.filter(isShownInBlock)
    const hiddenBuiltins = activeSequence.filter(column => !isShownInBlock(column))

    const onDragEnd = result => {
        const { source, destination } = result
        if (!destination || destination.droppableId !== source.droppableId) return
        if (source.index === destination.index) return
        if (source.droppableId === 'crs-template-order') {
            moveTemplate(source.index, destination.index)
            return
        }
        if (source.droppableId !== 'crs-block-columns' || source.index === destination.index) return
        const moving = shownSequence[source.index]
        const target = shownSequence[destination.index]
        if (!moving || !target) return
        apply(
            moveInTemplate(
                columns,
                groupOrder,
                templateRecords,
                activeKey,
                moving.key,
                target.key,
                destination.index > source.index,
            ),
        )
    }

    // Partly added fields first (the likely next click), then by name.
    const shared = useMemo(() => {
        const addedIn = entry =>
            Object.entries(entry.perTemplate).filter(([key, question]) =>
                fieldsOf(columns, key, templateRecords).some(
                    column => String(column.questionId) === String(question.id),
                ),
            ).length
        return sharedQuestions(chosen, questionsByTemplate)
            .map(entry => {
                const added = addedIn(entry)
                const total = Object.keys(entry.perTemplate).length
                let rank = 1
                if (added > 0 && added < total) rank = 0
                else if (added === total) rank = 2
                return { ...entry, rank }
            })
            .sort((a, b) => a.rank - b.rank || a.text.localeCompare(b.text))
    }, [chosen, questionsByTemplate, columns, templateRecords])
    const SHARED_ROWS = 6
    const sharedShown = showAllShared ? shared : shared.slice(0, SHARED_ROWS)

    const addSharedToAll = entry => {
        let next = columns
        Object.entries(entry.perTemplate).forEach(([key, question]) => {
            const has = fieldsOf(next, key, templateRecords).some(
                column => String(column.questionId) === String(question.id),
            )
            if (!has && fieldsOf(next, key, templateRecords).length < MAX_TEMPLATE_COLUMNS) {
                next = appendFields(next, groupOrder, templateRecords, key, [
                    newColumn(key, question),
                ])
            }
        })
        apply(next)
    }

    // Fields whose template is no longer chosen (deleted / no access, or older rows without a template).
    const strayColumns = (columns || []).filter(column => {
        if (column.builtin) return false
        const key = groupKeyOf(column, templateRecords)
        return !key || !chosen.includes(key)
    })

    const renderTemplateRow = option => {
        const key = String(option.value)
        const isChosen = chosen.includes(key)
        const count = usage ? usage[key] || 0 : null
        return (
            <Avail key={key}>
                <input
                    type="checkbox"
                    checked={isChosen}
                    onChange={event => toggleTemplate(key, event.target.checked)}
                />
                <span className="t">{highlight(nameOf(key), templateQuery)}</span>
                {count != null && (
                    <span className="cnt" style={count ? undefined : { color: '#d97706' }}>
                        {count ? `${count} audits` : 'no audits'}
                    </span>
                )}
            </Avail>
        )
    }

    const renderOrderRow = (key, index, drag) => {
        const count = fieldsOf(columns, key, templateRecords).length
        return (
            <Item
                $field
                ref={drag?.innerRef}
                {...(drag ? drag.draggableProps : {})}
                style={drag ? drag.draggableProps.style : undefined}
            >
                {drag && (
                    <span className="grip" {...drag.dragHandleProps}>
                        <DotsSixVertical size={16} />
                    </span>
                )}
                <span className="pos">{includeAllTemplates || !templateOrderSaved ? '•' : index + 1}</span>
                <span className="t">
                    {nameOf(key)}
                    <small>
                        {usage ? `${usage[key] || 0} audits in 30 days · ` : ''}
                        {count} field{count === 1 ? '' : 's'}
                    </small>
                </span>
                {!includeAllTemplates && (
                    <>
                        <IconBtn
                            type="button"
                            title="Move up"
                            disabled={index === 0}
                            onClick={() => moveTemplate(index, index - 1)}
                        >
                            <ArrowUp size={14} />
                        </IconBtn>
                        <IconBtn
                            type="button"
                            title="Move down"
                            disabled={index === chosen.length - 1}
                            onClick={() => moveTemplate(index, index + 1)}
                        >
                            <ArrowDown size={14} />
                        </IconBtn>
                    </>
                )}
                <IconBtn type="button" title="Remove" onClick={() => removeTemplate(key)}>
                    <X size={14} />
                </IconBtn>
            </Item>
        )
    }

    const renderColumnRow = (column, index, drag) => {
        const meta = column.builtin ? builtinMeta[column.key] : null
        const isEditing = editing === column.key
        let name = columnHeading(column)
        if (column.builtin && !name) name = `${meta?.title || column.key} (blank header)`
        let sub = 'Standard column · same in every block'
        if (!column.builtin) {
            const type = column.responseType ? `${column.responseType} question` : 'Question'
            sub = column.label ? `Renamed · question: ${column.questionText}` : type
            if (column.visible === false) sub += ' · photos only (no column)'
        }
        return (
            <Item
                $field={!column.builtin}
                ref={drag.innerRef}
                {...drag.draggableProps}
                style={drag.draggableProps.style}
            >
                <span className="grip" {...drag.dragHandleProps}>
                    <DotsSixVertical size={16} />
                </span>
                <span className="pos">{index + 1}</span>
                <span className="t">
                    {isEditing ? (
                        <input
                            className="ren"
                            placeholder={column.builtin ? builtinMeta[column.key]?.title : column.questionText}
                            ref={element => {
                                if (element && document.activeElement !== element) element.focus()
                            }}
                            value={editText}
                            maxLength={100}
                            onChange={event => setEditText(event.target.value)}
                            onBlur={() => commitRename(column)}
                            onKeyDown={event => {
                                if (event.key === 'Enter') {
                                    event.preventDefault()
                                    commitRename(column)
                                }
                                if (event.key === 'Escape') {
                                    renameCancelled.current = true
                                    setEditing(null)
                                }
                            }}
                        />
                    ) : (
                        <span
                            className="name"
                            role="button"
                            tabIndex={0}
                            title="Click to rename the header"
                            onClick={() => startRename(column)}
                            onKeyDown={event => {
                                if (event.key === 'Enter') startRename(column)
                            }}
                        >
                            {name}
                        </span>
                    )}
                    <small>{sub}</small>
                </span>
                {column.builtin && column.locked && <Tag>always</Tag>}
                {column.builtin && !column.locked && (
                    <IconBtn
                        type="button"
                        title="Hide in every block"
                        onClick={() => updateColumn(column.key, { visible: false })}
                    >
                        <Eye size={15} />
                    </IconBtn>
                )}
                {!column.builtin && (
                    <IconBtn
                        type="button"
                        $on={column.showPhotos}
                        title={
                            column.showPhotos
                                ? 'Photos of this question are shown in the cell'
                                : "Show this question's photos"
                        }
                        onClick={() =>
                            updateColumn(column.key, { showPhotos: !column.showPhotos })
                        }
                    >
                        <Camera size={15} weight={column.showPhotos ? 'fill' : 'regular'} />
                    </IconBtn>
                )}
                <IconBtn
                    type="button"
                    title="Move left"
                    disabled={index === 0}
                    onClick={() =>
                        apply(
                            stepInTemplate(columns, groupOrder, templateRecords, activeKey, column.key, -1),
                        )
                    }
                >
                    <ArrowUp size={14} />
                </IconBtn>
                <IconBtn
                    type="button"
                    title="Move right"
                    disabled={index === shownSequence.length - 1}
                    onClick={() =>
                        apply(
                            stepInTemplate(columns, groupOrder, templateRecords, activeKey, column.key, 1),
                        )
                    }
                >
                    <ArrowDown size={14} />
                </IconBtn>
                {!column.builtin ? (
                    <IconBtn type="button" title="Remove field" onClick={() => removeField(column)}>
                        <X size={14} />
                    </IconBtn>
                ) : (
                    <span style={{ width: 26 }} />
                )}
            </Item>
        )
    }

    const renderQuestionList = () => {
        const state = loadState[activeKey]
        if (state === 'loading' || (!state && !activeQuestions.length)) {
            return <div style={{ padding: 10, color: '#6b7280' }}>Loading questions…</div>
        }
        if (state === 'error') {
            return (
                <div style={{ padding: 10 }}>
                    <div className="field-error">Couldn&apos;t load questions for this template.</div>
                    <AppButton
                        type="button"
                        basic
                        className="mt-2"
                        onClick={() => {
                            setLoadState(prev => ({ ...prev, [activeKey]: undefined }))
                        }}
                    >
                        Retry
                    </AppButton>
                </div>
            )
        }
        const q = fieldQuery.trim().toLowerCase()
        const rows = []
        let lastSection = null
        activeQuestions.forEach(question => {
            if (q && !question.text.toLowerCase().includes(q)) return
            if (question.section && question.section !== lastSection) {
                rows.push(<SectionLabel key={`s-${question.section}-${question.id}`}>{question.section}</SectionLabel>)
                lastSection = question.section
            }
            const inEmail = activeQuestionIds.has(String(question.id))
            const isPicked = Boolean(picked[question.id])
            const disabled = !inEmail && !isPicked && left <= 0
            const also = otherTemplatesWith(question.text)
            rows.push(
                <Avail key={question.id} $dim={inEmail || disabled} $disabled={inEmail || disabled}>
                    <input
                        type="checkbox"
                        checked={inEmail || isPicked}
                        disabled={inEmail || disabled}
                        onChange={event =>
                            setPicked(prev => {
                                const next = { ...prev }
                                if (event.target.checked) next[question.id] = true
                                else delete next[question.id]
                                return next
                            })
                        }
                    />
                    <span className="t">
                        {highlight(question.text, fieldQuery)}
                        {inEmail && <span style={{ color: '#6b7280' }}> · in the email</span>}
                        {also.length > 0 && <span className="also">also in {also.join(', ')}</span>}
                    </span>
                    {question.responseType && <TypePill>{question.responseType}</TypePill>}
                </Avail>,
            )
        })
        if (!activeQuestions.length) {
            return <div style={{ padding: 10, color: '#6b7280' }}>This template has no answer fields.</div>
        }
        if (!rows.length) {
            return <div style={{ padding: 10, color: '#6b7280' }}>No question matches that search.</div>
        }
        return rows
    }

    return (
        <Form.Field error={Boolean(templateError)}>
            <label>
                What&apos;s in the email <NewBadge>NEW</NewBadge>
            </label>
            <Wrap>
                <DragDropContext onDragEnd={onDragEnd}>
                    <StepHead>
                        <span className="num">1</span>
                        <div>
                            <h4>Pick the templates</h4>
                            <small>
                                Tick templates on the left. On the right, drag them into the order their
                                blocks appear in the email.
                            </small>
                        </div>
                    </StepHead>
                    <Two>
                        <Box>
                            <div className="box-h" style={{ display: 'block', fontWeight: 400 }}>
                                <Checkbox
                                    label="All templates (every completed audit, now and in the future)"
                                    checked={Boolean(includeAllTemplates)}
                                    onChange={(_, { checked }) => setAllTemplates(checked)}
                                    style={{ marginBottom: 8 }}
                                />
                                <SearchInput
                                    value={templateQuery}
                                    placeholder="Search templates…"
                                    onChange={event => setTemplateQuery(event.target.value)}
                                />
                            </div>
                            <div className="box-b">
                                {filteredTemplates.map(renderTemplateRow)}
                                {!filteredTemplates.length && (
                                    <div style={{ padding: 10, color: '#6b7280' }}>
                                        {templateOptions.length ? 'No template matches.' : 'Loading templates…'}
                                    </div>
                                )}
                            </div>
                            <div className="box-f">
                                {usage ? 'Sorted by audits in the last 30 days' : 'Sorted by name'}
                            </div>
                        </Box>
                        <Box>
                            <div className="box-h">
                                <span>
                                    {includeAllTemplates
                                        ? 'Templates with their own fields'
                                        : 'In the email, in this order'}
                                </span>
                                <Pill>{chosen.length} selected</Pill>
                            </div>
                            <div className="box-b">
                                {includeAllTemplates ? (
                                    chosen.map((key, index) => (
                                        <React.Fragment key={key}>{renderOrderRow(key, index, null)}</React.Fragment>
                                    ))
                                ) : (
                                    <Droppable droppableId="crs-template-order" type="crs-template">
                                        {provided => (
                                            <div ref={provided.innerRef} {...provided.droppableProps}>
                                                {chosen.map((key, index) => (
                                                    <Draggable key={key} draggableId={`crs-tpl-${key}`} index={index}>
                                                        {drag => renderOrderRow(key, index, drag)}
                                                    </Draggable>
                                                ))}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                )}
                                {!chosen.length && (
                                    <div style={{ padding: 12, color: '#6b7280' }}>
                                        {includeAllTemplates
                                            ? 'Every template is included. Tick a template on the left to give it fields of its own.'
                                            : 'Tick a template on the left.'}
                                    </div>
                                )}
                            </div>
                            <div className="box-f">
                                {includeAllTemplates && 'With All templates the blocks follow the newest audit.'}
                                {!includeAllTemplates && !templateOrderSaved && (
                                    <span>
                                        <strong>Order not set:</strong> blocks follow the newest audit. Drag or use
                                        ↑ ↓ to set an order.
                                    </span>
                                )}
                                {!includeAllTemplates && templateOrderSaved && (
                                    <>
                                        <span>Drag or use ↑ ↓. The first template is the first block.</span>
                                        <button
                                            type="button"
                                            onClick={() => change({ templateOrderSaved: false })}
                                            style={{ border: 'none', background: 'none', color: '#263BA6', fontWeight: 600, padding: 0, cursor: 'pointer' }}
                                        >
                                            Use newest audit first instead
                                        </button>
                                    </>
                                )}
                            </div>
                        </Box>
                    </Two>
                    {templateError && <div className="field-error">{templateError}</div>}

                    <StepHead>
                        <span className="num">2</span>
                        <div>
                            <h4>Pick fields and set the column order</h4>
                            <small>
                                One tab per template, up to {MAX_TEMPLATE_COLUMNS} fields each. Standard
                                columns (grey) can sit anywhere between the fields.
                            </small>
                        </div>
                    </StepHead>

                    {shared.length > 0 && (
                        <Common>
                            <strong>Same field in several templates</strong>
                            <div style={{ color: '#065f46', fontSize: 12, marginBottom: 4 }}>
                                Add a field to every template that has it. Each template keeps its own column.
                            </div>
                            {sharedShown.map(entry => {
                                const states = Object.entries(entry.perTemplate).map(([key, question]) => {
                                    const fields = fieldsOf(columns, key, templateRecords)
                                    const added = fields.some(
                                        column => String(column.questionId) === String(question.id),
                                    )
                                    return { key, question, added, full: !added && fields.length >= MAX_TEMPLATE_COLUMNS }
                                })
                                const missing = states.filter(s => !s.added && !s.full)
                                return (
                                    <div className="crow" key={normalizeText(entry.text)}>
                                        <span className="cn">{entry.text}</span>
                                        {states.map(s => {
                                            if (s.added) return <Chip key={s.key} as="span" $state="ok">✓ {nameOf(s.key)}</Chip>
                                            if (s.full) return <Chip key={s.key} as="span">{nameOf(s.key)} · full</Chip>
                                            return (
                                                <Chip
                                                    key={s.key}
                                                    type="button"
                                                    $state="add"
                                                    onClick={() => apply(addToTemplate(s.key, s.question))}
                                                >
                                                    + {nameOf(s.key)}
                                                </Chip>
                                            )
                                        })}
                                        {missing.length > 1 && (
                                            <AppButton type="button" size="mini" basic onClick={() => addSharedToAll(entry)}>
                                                Add to all
                                            </AppButton>
                                        )}
                                    </div>
                                )
                            })}
                            {shared.length > SHARED_ROWS && (
                                <button
                                    type="button"
                                    onClick={() => setShowAllShared(value => !value)}
                                    style={{ border: 'none', background: 'none', color: '#047857', fontWeight: 700, padding: '4px 0 0', cursor: 'pointer' }}
                                >
                                    {showAllShared ? 'Show fewer' : `Show all ${shared.length}`}
                                </button>
                            )}
                        </Common>
                    )}

                    {chosen.length > 0 ? (
                        <>
                            <Tabs>
                                {chosen.map(key => {
                                    const count = fieldsOf(columns, key, templateRecords).length
                                    let tone = ''
                                    if (count >= MAX_TEMPLATE_COLUMNS) tone = 'warn'
                                    else if (count) tone = 'brand'
                                    return (
                                        <Tab
                                            key={key}
                                            type="button"
                                            $on={key === activeKey}
                                            onClick={() => {
                                                setActiveKey(key)
                                                setPicked({})
                                                setFieldQuery('')
                                                setEditing(null)
                                            }}
                                        >
                                            {nameOf(key)}
                                            <Pill $tone={tone}>
                                                {count}/{MAX_TEMPLATE_COLUMNS}
                                            </Pill>
                                        </Tab>
                                    )
                                })}
                            </Tabs>
                            {activeKey && (
                                <Two>
                                    <Box>
                                        <div className="box-h" style={{ display: 'block' }}>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    gap: 8,
                                                    marginBottom: 8,
                                                    flexWrap: 'wrap',
                                                }}
                                            >
                                                <span>Questions in {nameOf(activeKey)}</span>
                                                <Pill $tone={left <= 0 ? 'warn' : ''}>
                                                    {Math.max(left, 0)} of {MAX_TEMPLATE_COLUMNS} left
                                                </Pill>
                                            </div>
                                            <SearchInput
                                                value={fieldQuery}
                                                placeholder="Search questions…"
                                                onChange={event => setFieldQuery(event.target.value)}
                                            />
                                        </div>
                                        <div className="box-b">{renderQuestionList()}</div>
                                        <div className="box-f">
                                            <span>Tick several, then add them.</span>
                                            <AppButton
                                                type="button"
                                                primary
                                                size="small"
                                                disabled={!pickedCount}
                                                onClick={addPicked}
                                            >
                                                Add {pickedCount || ''} field{pickedCount === 1 ? '' : 's'} →
                                            </AppButton>
                                        </div>
                                    </Box>
                                    <Box>
                                        <div className="box-h">
                                            <span>Columns in this block, left to right</span>
                                            <span style={{ fontWeight: 400, color: '#6b7280', fontSize: 12 }}>
                                                drag or ↑ ↓ · click a name to rename
                                            </span>
                                        </div>
                                        <div className="box-b">
                                            <Droppable droppableId="crs-block-columns" type="crs-column">
                                                {provided => (
                                                    <div ref={provided.innerRef} {...provided.droppableProps}>
                                                        {shownSequence.map((column, index) => (
                                                            <Draggable
                                                                key={column.key}
                                                                draggableId={`crs-col-${activeKey}-${column.key}`}
                                                                index={index}
                                                                isDragDisabled={editing === column.key}
                                                            >
                                                                {drag => renderColumnRow(column, index, drag)}
                                                            </Draggable>
                                                        ))}
                                                        {provided.placeholder}
                                                    </div>
                                                )}
                                            </Droppable>
                                            {hiddenBuiltins.length > 0 && (
                                                <div style={{ padding: '8px 4px 2px' }}>
                                                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 5 }}>
                                                        Hidden standard columns (click to show in every block)
                                                    </div>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                                                        {hiddenBuiltins.map(column => (
                                                            <Chip
                                                                key={column.key}
                                                                type="button"
                                                                $state="add"
                                                                onClick={() => updateColumn(column.key, { visible: true })}
                                                            >
                                                                <EyeSlash size={11} style={{ marginRight: 3, verticalAlign: -1 }} />
                                                                {columnHeading(column) || builtinMeta[column.key]?.title}
                                                            </Chip>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <div className="box-f">
                                            <span>
                                                {activeFields.length} field{activeFields.length === 1 ? '' : 's'} ·{' '}
                                                {Math.max(0, MAX_TEMPLATE_COLUMNS - activeFields.length)} left
                                            </span>
                                            {chosen.length > 1 && (
                                                <AppButton
                                                    type="button"
                                                    basic
                                                    size="mini"
                                                    title="Put the same fields (matched by question text) in the same order in the other templates"
                                                    onClick={() => {
                                                        apply(
                                                            copyOrderToOthers(
                                                                columns,
                                                                groupOrder,
                                                                templateRecords,
                                                                activeKey,
                                                                chosen,
                                                            ),
                                                        )
                                                        setInfo(
                                                            'Same order applied to the other templates. Fields they do not have are skipped; their own extra fields go last.',
                                                        )
                                                    }}
                                                >
                                                    Copy this order to other templates
                                                </AppButton>
                                            )}
                                        </div>
                                    </Box>
                                </Two>
                            )}
                        </>
                    ) : (
                        <Notice $tone="info">
                            {includeAllTemplates
                                ? 'Every template shows the standard columns. Tick a template above to give it fields of its own.'
                                : 'Pick at least one template above.'}
                        </Notice>
                    )}

                    {strayColumns.length > 0 && (
                        <Notice $tone="warn" style={{ display: 'block' }}>
                            <strong>Saved fields of templates that are not in this email</strong>
                            <div style={{ margin: '4px 0 6px' }}>
                                {includeAllTemplates
                                    ? 'They stay on the schedule.'
                                    : 'They are removed when you save, unless you tick their template above.'}
                            </div>
                            {strayColumns.map(column => (
                                <div
                                    key={column.key}
                                    style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}
                                >
                                    <span style={{ flex: 1 }}>
                                        {columnHeading(column)}{' '}
                                        <span style={{ opacity: 0.75 }}>
                                            ({column.templateName || 'template not available'})
                                        </span>
                                    </span>
                                    <IconBtn type="button" title="Remove field" onClick={() => removeField(column)}>
                                        <X size={14} />
                                    </IconBtn>
                                </div>
                            ))}
                        </Notice>
                    )}

                    {info && (
                        <Notice $tone="info">
                            <span>{info}</span>
                            <button type="button" onClick={() => setInfo('')}>
                                OK
                            </button>
                        </Notice>
                    )}
                    {undo && (
                        <Notice $tone="dark">
                            <span>{undo.message}</span>
                            <button
                                type="button"
                                onClick={() => {
                                    undo.restore()
                                    setUndo(null)
                                }}
                            >
                                Undo
                            </button>
                        </Notice>
                    )}
                </DragDropContext>
            </Wrap>
        </Form.Field>
    )
}
